import { useCallback, useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import {
  getAiTutorThumbsDown,
  getGetAiTutorThumbsDownQueryKey,
} from '@/common/api/endpoints/reports/reports';
import type { GetAiTutorThumbsDownParams } from '@/common/api/models/getAiTutorThumbsDownParams';
import type { ThumbsDownList } from '@/common/api/models/thumbsDownList';

import type { MetricsReportFilters } from '@/admin/common/reportFilters/reportFilterDefaults';

import type { ThumbsDownRowView } from './thumbsDownRowView';

export type ThumbsDownReasonsStatus = 'loading' | 'ready' | 'error';

export const THUMBS_DOWN_PAGE_SIZE = 20;

const LIST_AXIOS = { paramsSerializer: { indexes: null } } as const;

export type UseThumbsDownReasonsResult = {
  rows: ThumbsDownRowView[];
  count: number;
  status: ThumbsDownReasonsStatus;
  hasMore: boolean;
  loadMoreCount: number;
  isLoadingMore: boolean;
  loadMoreError: boolean;
  refetch: () => void;
  retryLoadMore: () => void;
  loadMore: () => void;
};

function formatStudentName(
  student: { firstname?: string | null; lastname?: string | null } | undefined,
  unknownLabel: string,
): string {
  const last = student?.lastname?.trim();
  const first = student?.firstname?.trim();
  if (last && first) return `${last}, ${first}`;
  return unknownLabel;
}

function formatRowDate(at: string): string {
  const parsed = new Date(at);
  if (Number.isNaN(parsed.getTime())) return '';
  return parsed.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

/** Maps Orval thumbs-down list items to plain row view models. */
export function mapThumbsDownRows(
  data: ThumbsDownList | null | undefined,
  labels: { unknownStudent: string; unknownModule: string; unknownTask: string },
): ThumbsDownRowView[] {
  return (data?.items ?? []).map((item) => ({
    sessionId: item.sessionId,
    voteId: item.voteId,
    moduleId: item.module?.id ?? '',
    reason: item.reason,
    studentLabel: formatStudentName(item.student, labels.unknownStudent),
    moduleLabel: item.module?.title?.trim() || labels.unknownModule,
    taskLabel: item.task?.title?.trim() || labels.unknownTask,
    dateLabel: formatRowDate(item.at),
  }));
}

function buildFilterParams(appliedFilters: MetricsReportFilters): GetAiTutorThumbsDownParams {
  return {
    gated: appliedFilters.gated,
    from: appliedFilters.from,
    to: appliedFilters.to,
    excludePasteHeavy: appliedFilters.excludePasteHeavy,
    excludeFirstTryPasses: appliedFilters.excludeFirstTryPasses,
    ...(appliedFilters.moduleIds.length > 0 ? { moduleIds: appliedFilters.moduleIds } : {}),
  };
}

/**
 * Loads individual thumbs-down votes for the AI Tutor dashboard list.
 * Orval types stop in mapThumbsDownRows.
 */
export function useThumbsDownReasons(appliedFilters: MetricsReportFilters): UseThumbsDownReasonsResult {
  const { t } = useTranslation();

  const filterParams = useMemo(
    () => buildFilterParams(appliedFilters),
    [appliedFilters],
  );

  const {
    data,
    isLoading,
    isError,
    isFetchingNextPage,
    isFetchNextPageError,
    fetchNextPage,
    refetch,
  } = useInfiniteQuery({
    queryKey: getGetAiTutorThumbsDownQueryKey(filterParams),
    queryFn: ({ pageParam, signal }) => getAiTutorThumbsDown(
      {
        ...filterParams,
        page: pageParam,
        pageSize: THUMBS_DOWN_PAGE_SIZE,
      },
      { signal, ...LIST_AXIOS },
    ),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const pagination = lastPage?.data?.pagination;
      if (!pagination) return undefined;
      const loaded = allPages.flatMap((page) => page?.data?.data?.items ?? []).length;
      if (loaded >= pagination.total) return undefined;
      return pagination.page + 1;
    },
    retry: false,
  });

  const labels = useMemo(
    () => ({
      unknownStudent: t('admin.analytics.feedbackHelpfulness.unknownStudent'),
      unknownModule: t('admin.analytics.feedbackHelpfulness.unknownModule'),
      unknownTask: t('admin.analytics.feedbackHelpfulness.unknownTask'),
    }),
    [t],
  );

  const flattenedItems = useMemo(
    () => data?.pages.flatMap((page) => page?.data?.data?.items ?? []) ?? [],
    [data?.pages],
  );

  const rows = useMemo(
    () => mapThumbsDownRows({ items: flattenedItems }, labels),
    [flattenedItems, labels],
  );

  const total = data?.pages[0]?.data?.pagination?.total ?? 0;
  const hasMore = total > rows.length;
  const remaining = Math.max(0, total - rows.length);
  const loadMoreCount = Math.min(THUMBS_DOWN_PAGE_SIZE, remaining);

  const hasLoadedPages = (data?.pages?.length ?? 0) > 0;

  const status: ThumbsDownReasonsStatus = useMemo(() => {
    if (isLoading && !hasLoadedPages) return 'loading';
    if (isError && !hasLoadedPages) return 'error';
    return 'ready';
  }, [hasLoadedPages, isError, isLoading]);

  const loadMoreError = isFetchNextPageError;
  const isLoadingMore = isFetchingNextPage;

  const loadMore = useCallback(() => {
    if (!hasMore || isFetchingNextPage) return;
    fetchNextPage().catch(() => undefined);
  }, [fetchNextPage, hasMore, isFetchingNextPage]);

  const retryLoadMore = useCallback(() => {
    fetchNextPage().catch(() => undefined);
  }, [fetchNextPage]);

  const refetchList = useCallback(() => {
    refetch().catch(() => undefined);
  }, [refetch]);

  return {
    rows,
    count: hasLoadedPages ? total : 0,
    status,
    hasMore,
    loadMoreCount,
    isLoadingMore,
    loadMoreError,
    refetch: refetchList,
    retryLoadMore,
    loadMore,
  };
}
